import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

interface BookingInfoSectionProps {
  fundName: string | undefined;
}

export const BookingInfoSection: React.FC<BookingInfoSectionProps> = ({ fundName }) => {
  const { t } = useTranslation(['bookings', 'common']);

  // Only render if we have a fund name
  if (!fundName) return null;

  // DYPA specific content
  if (fundName === 'ΔΥΠΑ') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800 mb-3">
              {t('bookingsBox.dypaInfo.reminder', { 
                ns: 'bookings', 
                defaultValue: 'Υπενθυμίζουμε ότι η συμπλήρωση της παραπάνω φόρμας δεν αποτελεί κατοχύρωση της εγγραφής σας.'
              })}
            </p>
            <p className="text-amber-800 mb-2">
              {t('bookingsBox.dypaInfo.steps', { 
                ns: 'bookings', 
                defaultValue: 'Θα πρέπει να ακολουθήσετε και τα εξής τρία (3) βήματα για την κατοχύρωση της θέσης σας:'
              })}
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-amber-800">
              <li>
                {t('bookingsBox.dypaInfo.step1', { 
                  ns: 'bookings', 
                  defaultValue: 'Υποβολή ηλεκτρονικής αίτησης στην πλατφόρμα του ΔΥΠΑ (πρώην ΟΑΕΔ). Περισσότερες πληροφορίες'
                })} <a 
                  href="https://www.dypa.gov.gr/storage/kataskhnotiko-2024/odhgies-aithshs-dikaiuxu-2024.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-600 underline hover:text-amber-800"
                >
                  {t('bookingsBox.dypaInfo.here', { ns: 'bookings', defaultValue: 'εδώ' })}
                </a>
              </li>
              <li>
                {t('bookingsBox.dypaInfo.step2', { 
                  ns: 'bookings', 
                  defaultValue: 'Αποστολή αριθμού πρωτοκόλλου: Μετά την υποβολή της αίτησης στον ΔΥΠΑ, θα πρέπει να μας κοινοποιήσετε τον αριθμό πρωτοκόλλου της. (Η μη κοινοποίηση του αριθμού πρωτοκόλλου ακυρώνει την προκράτηση της θέσης σας.)'
                })}
              </li>
              <li>
                {t('bookingsBox.dypaInfo.step3', { 
                  ns: 'bookings', 
                  defaultValue: 'Οριστική επιβεβαίωση θέσης: Μετά την ανακοίνωση των οριστικών αποτελεσμάτων και εφόσον έχετε εγκριθεί (δηλαδή, έχετε αριθμό επιταγής), θα πρέπει εντός 5 ημερών να επιβεβαιώσετε την προκράτησή σας στην κατασκήνωσή μας. (Θα λάβετε οδηγίες για τη διαδικασία.)'
                })}
              </li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Ιδιωτικά specific content - TODO: make pricing table dynamic
  if (fundName === 'Ιδιωτικά') {
    return null;
  }

  // ΕΦΚΑ specific content
  if (fundName === 'ΕΦΚΑ') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">
              {t('bookingsBox.efkaInfo.reminder', {
                ns: 'bookings',
                defaultValue: 'Υπενθυμίζουμε ότι η συμπλήρωση της παραπάνω φόρμας δεν αποτελεί κατοχύρωση της συμμετοχής σας μέσω του ΕΦΚΑ. Συμβουλευτείτε τις οδηγίες που απαιτούνται από τον ΕΦΚΑ πατώντας στο link'
              })}{' '}
              «<a
                href="https://ranch.gr/efka"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 underline hover:text-amber-800"
              >
                {t('bookingsBox.efkaInfo.here', { ns: 'bookings', defaultValue: 'εδώ' })}
              </a>»
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Λοιποί φορείς και Συνεργασίες specific content
  if (fundName === 'Λοιποί φορείς και Συνεργασίες') {
    return (
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-4 text-sm relative">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800 mb-3">
              {t('bookingsBox.otherFundsInfo.reminder', { 
                ns: 'bookings', 
                defaultValue: 'Υπενθυμίζουμε ότι η συμπλήρωση της παραπάνω φόρμας δεν αποτελεί κατοχύρωση της εγγραφής σας.'
              })}
            </p>
            
            <p className="text-amber-800">
              {t('bookingsBox.otherFundsInfo.communication', { 
                ns: 'bookings', 
                defaultValue: 'Πριν από την εγγραφή, θα πρέπει να προηγηθεί επικοινωνία με εκπρόσωπο της κατασκήνωσης, καθώς ο φορέας σας ενδέχεται να απαιτεί πρόσθετες διαδικασίες εγγραφής.'
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}; 